import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseURL } from '../constants/base.url';

export interface DashboardStats {
  filterDate: string | null;
  totalPatients: number;
  activeProtocols: number;
  totalCompletedOrders: number;
  totalOnHoldOrders: number;
}

export interface DueProtocol {
  _id: string;
  patientId: string | null;
  patientName: string;
  phone: string;
  patientStatus: string;
  fulfillmentPreference: string;
  discountPercent: number;

  templateId: string | null;
  templateName: string;
  templateSlug: string;

  frequencyMonths: number;
  startDate: string;
  nextDueDate: string;
  dueState: 'DUE' | 'OVERDUE';
  status: string;
  notes: string;

  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  total: number;
  totalPages: number;
  currentPage: number;
  limit: number;
}

export interface DueProtocolsResponse {
  protocols: DueProtocol[];
  pagination: Pagination;
}

export interface ApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

export interface OrderStatusOverview {
  filterDate: string | null;
  totalOrders: number;
  pendingOrders: number;
  processingOrders: number;
  deliveredOrders: number;
  onHoldOrders: number;
  chart: {
    labels: string[];
    series: number[];
  };
}

export interface RecentOrder {
  _id: string;
  orderNo: string;
  status: string;
  fulfillmentMethod: string;
  currency: string;
  grandTotal: number;
  discountPercent: number;
  createdAt: string;

  patientId: string | null;
  patientName: string;
  patientPhone: string;
  patientEmail: string;
  patientStatus: string;
  fulfillmentPreference: string;

  trackingNo: string;
}

export interface RecentOrdersResponse {
  orders: RecentOrder[];
  total: number;
  limit: number;
}

export interface PaymentIssue {
  issueType: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  subtitle: string;
  amount: number;
  currency: string;

  patientId?: string | null;
  patientName?: string;

  invoiceId?: string | null;
  invoiceNo?: string;

  orderId?: string | null;
  orderNo?: string;

  billingProfileId?: string | null;
  paymentMethodId?: string | null;

  reason?: string;
  dueDate?: string | null;
  createdAt?: string | null;

  actionLabel: string;
}

export interface PaymentIssuesResponse {
  issues: PaymentIssue[];
  total: number;
  limit: number;
}


@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(private http: HttpClient) { }

  /**
   * Get combined dashboard stats
   * Example:
   * /admin/dashboard/stats
   * /admin/dashboard/stats?date=2026-03-09
   */
  getDashboardStats(date?: string): Observable<ApiResponse<DashboardStats>> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    return this.http.get<ApiResponse<DashboardStats>>(
      `${baseURL}admin/dashboard/stats`,
      { params }
    );
  }

  /**
   * Get due assigned protocols for dashboard table
   * Example:
   * /admin/dashboard/due-protocols?page=1&limit=10&q=ali&sort=nextDueDate:asc
   */
  getDueProtocols(paramsObj?: {
    page?: number;
    limit?: number;
    q?: string;
    sort?: string;
  }): Observable<ApiResponse<DueProtocolsResponse>> {
    let params = new HttpParams();

    if (paramsObj?.page) {
      params = params.set('page', paramsObj.page.toString());
    }

    if (paramsObj?.limit) {
      params = params.set('limit', paramsObj.limit.toString());
    }

    if (paramsObj?.q) {
      params = params.set('q', paramsObj.q);
    }

    if (paramsObj?.sort) {
      params = params.set('sort', paramsObj.sort);
    }

    return this.http.get<ApiResponse<DueProtocolsResponse>>(
      `${baseURL}admin/dashboard/due-protocols`,
      { params }
    );
  }

  /**
 * Get order status overview for donut chart
 * Example:
 * /admin/dashboard/order-status-overview
 * /admin/dashboard/order-status-overview?date=2026-03-09
 */
  getOrderStatusOverview(date?: string): Observable<ApiResponse<OrderStatusOverview>> {
    let params = new HttpParams();

    if (date) {
      params = params.set('date', date);
    }

    return this.http.get<ApiResponse<OrderStatusOverview>>(
      `${baseURL}admin/dashboard/order-status-overview`,
      { params }
    );
  }

  /**
 * Get recent orders for dashboard table
 * Example:
 * /admin/dashboard/recent-orders
 * /admin/dashboard/recent-orders?limit=5
 * /admin/dashboard/recent-orders?date=2026-03-09&limit=10
 */
  getRecentOrders(paramsObj?: {
    date?: string;
    limit?: number;
  }): Observable<ApiResponse<RecentOrdersResponse>> {
    let params = new HttpParams();

    if (paramsObj?.date) {
      params = params.set('date', paramsObj.date);
    }

    if (paramsObj?.limit) {
      params = params.set('limit', paramsObj.limit.toString());
    }

    return this.http.get<ApiResponse<RecentOrdersResponse>>(
      `${baseURL}admin/dashboard/recent-orders`,
      { params }
    );
  }

  /**
 * Get payment issues for dashboard card
 * Example:
 * /admin/dashboard/payment-issues
 * /admin/dashboard/payment-issues?limit=10
 */
  getPaymentIssues(paramsObj?: {
    limit?: number;
  }): Observable<ApiResponse<PaymentIssuesResponse>> {
    let params = new HttpParams();

    if (paramsObj?.limit) {
      params = params.set('limit', paramsObj.limit.toString());
    }

    return this.http.get<ApiResponse<PaymentIssuesResponse>>(
      `${baseURL}admin/dashboard/payment-issues`,
      { params }
    );
  }
}
